import { useDebounceFn } from '@reactuses/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { client, useAction } from '@cold-ambulance/ui';

const STORAGE_KEY_PREFIX = 'cold-ambulance:request-draft';

type LocalDraft = {
  data: Record<string, unknown>;
  updatedAt: string;
  syncState: 'pending-create' | 'synced';
};

function getStorageKey(draftId: string) {
  return `${STORAGE_KEY_PREFIX}:${draftId}`;
}

export function getLocalDraft(draftId: string): LocalDraft | null {
  try {
    const raw = localStorage.getItem(getStorageKey(draftId));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<LocalDraft>;

    return {
      data: parsed.data ?? {},
      updatedAt: parsed.updatedAt ?? new Date().toISOString(),
      syncState: parsed.syncState ?? 'synced',
    };
  } catch {
    return null;
  }
}

export function clearLocalDraft(draftId: string) {
  localStorage.removeItem(getStorageKey(draftId));
}

function saveLocalDraft(
  draftId: string,
  values: Record<string, unknown>,
  options?: {
    updatedAt?: string;
    syncState?: LocalDraft['syncState'];
  },
) {
  try {
    localStorage.setItem(
      getStorageKey(draftId),
      JSON.stringify({
        data: values,
        updatedAt: options?.updatedAt ?? new Date().toISOString(),
        syncState: options?.syncState ?? 'synced',
      } satisfies LocalDraft),
    );
  } catch {
    // localStorage quota exceeded — server sync is the fallback
  }
}

export type DraftSaveStatus = 'idle' | 'saving' | 'saved';

export function useRequestDraftAutosave(
  form: {
    store: {
      state: { values: Record<string, unknown>; isDirty: boolean };
      subscribe: (cb: () => void) => { unsubscribe: () => void };
    };
  },
  options: {
    draftId?: string;
    onDraftCreated?: (draftId: string, values: Record<string, unknown>) => void;
  } = {},
) {
  const queryClient = useQueryClient();
  const updateDraft = useAction('PATCH /me/request-drafts/{id}', {
    invalidate: ['GET /me/request-drafts', 'GET /me/request-drafts/{id}'],
  });
  const mounted = useRef(false);
  const draftIdRef = useRef<string | undefined>(options.draftId);
  const syncStateRef = useRef<LocalDraft['syncState']>(
    options.draftId ? 'synced' : 'pending-create',
  );
  const inFlightSaveRef = useRef<Promise<void> | null>(null);
  const [status, setStatus] = useState<DraftSaveStatus>('idle');
  const savedTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    draftIdRef.current = options.draftId;
    syncStateRef.current = options.draftId ? 'synced' : 'pending-create';
  }, [options.draftId]);

  function setSavedStatus() {
    setStatus('saved');
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setStatus('idle'), 2000);
  }

  function ensureDraftId(values: Record<string, unknown>) {
    if (draftIdRef.current) {
      return draftIdRef.current;
    }

    const localDraftId = crypto.randomUUID();
    draftIdRef.current = localDraftId;
    syncStateRef.current = 'pending-create';
    saveLocalDraft(localDraftId, values, {
      syncState: 'pending-create',
    });
    options.onDraftCreated?.(localDraftId, values);
    return localDraftId;
  }

  async function invalidateDraftQueries(draftId: string) {
    await queryClient.invalidateQueries({
      predicate(query): boolean {
        return (
          (query.meta?.endpoint === 'GET /me/request-drafts' ||
            (query.meta?.endpoint === 'GET /me/request-drafts/{id}' &&
              query.meta?.input &&
              typeof query.meta.input === 'object' &&
              'id' in query.meta.input &&
              query.meta.input.id === draftId)) === true
        );
      },
    });
  }

  async function persistDraft(
    draftId: string,
    values: Record<string, unknown>,
  ) {
    if (syncStateRef.current === 'pending-create') {
      const created = await client.request('POST /me/request-drafts', {
        id: draftId,
        data: values,
      } as never);

      syncStateRef.current = 'synced';
      saveLocalDraft(draftId, values, {
        updatedAt: created.updatedAt,
        syncState: 'synced',
      });
      await invalidateDraftQueries(draftId);
      return;
    }

    const updated = await updateDraft.mutateAsync({
      id: draftId,
      data: values,
    });

    saveLocalDraft(draftId, values, {
      updatedAt: updated.updatedAt,
      syncState: 'synced',
    });
  }

  async function prepareDraftForSubmit(values: Record<string, unknown>) {
    const draftId = draftIdRef.current;
    if (!draftId) {
      return undefined;
    }

    if (inFlightSaveRef.current) {
      await inFlightSaveRef.current;
    }

    if (syncStateRef.current === 'pending-create') {
      setStatus('saving');
      saveLocalDraft(draftId, values, {
        syncState: 'pending-create',
      });
      const pendingSave = persistDraft(draftId, values);
      inFlightSaveRef.current = pendingSave.finally(() => {
        if (inFlightSaveRef.current === pendingSave) {
          inFlightSaveRef.current = null;
        }
      });
      await pendingSave;
      setSavedStatus();
    }

    return draftIdRef.current;
  }

  const { run: debouncedSave, cancel } = useDebounceFn(
    async (values: Record<string, unknown>) => {
      setStatus('saving');
      const draftId = ensureDraftId(values);
      saveLocalDraft(draftId, values, {
        syncState: syncStateRef.current,
      });

      try {
        const pendingSave = persistDraft(draftId, values);
        inFlightSaveRef.current = pendingSave.finally(() => {
          if (inFlightSaveRef.current === pendingSave) {
            inFlightSaveRef.current = null;
          }
        });
        await pendingSave;
        setSavedStatus();
      } catch {
        setStatus('idle');
      }
    },
    2000,
  );

  useEffect(() => {
    const sub = form.store.subscribe(() => {
      if (!mounted.current) {
        mounted.current = true;
        return;
      }
      if (!form.store.state.isDirty) return;
      debouncedSave(form.store.state.values);
    });

    return () => {
      cancel();
      clearTimeout(savedTimer.current);
      sub.unsubscribe();
    };
  }, []);

  return { status, prepareDraftForSubmit };
}
